import * as contentActions from './content.actions';

import { Actions, ofType, createEffect } from '@ngrx/effects';
import {
  ContentSection,
  ContentSectionGroupPaged,
  ContentSectionPaged,
  ContentPagePaged,
  ContentPage,
  ContentSectionGroup,
} from '@nrwl/rx-service-content-editor';
import { EMPTY, Observable, of, forkJoin } from 'rxjs';
import { catchError, map, mergeMap, withLatestFrom, switchMap } from 'rxjs/operators';

import { Action, Store, select } from '@ngrx/store';
import { ContentEditorService } from 'libs/rx-services/src/lib/api/content-editor/content-editor.service';
import { Injectable, inject } from '@angular/core';
import { ContentState } from './content.state';
import { getState } from './content.selectors';
import { SnackbarService } from '@nrwl/snackbar-services';
import { moveItemInArray } from '@angular/cdk/drag-drop';
import { SearchType } from '@nrwl/rx-models';
import { de } from 'date-fns/locale';

@Injectable()
export class ContentStoreEffects {
  private actions$ = inject(Actions);
  private contentEditorService = inject(ContentEditorService);
  private store = inject(Store<ContentState>);
  private _snackbarService = inject(SnackbarService);

  LoadCategories$ = createEffect(() =>
    this.actions$.pipe(
      ofType<contentActions.LoadCategories>(contentActions.ContentActionTypes.LoadCategories),
      mergeMap((action: any) =>
        this.contentEditorService.getContentSectionGroups().pipe(
          map((result: ContentSectionGroupPaged) => {
            return new contentActions.LoadCategoriesSuccess(result); //
          }),
          catchError((err: any) => of(new contentActions.Error(err))),
        ),
      ),
    ),
  );

  SearchCategories$ = createEffect(() =>
    this.actions$.pipe(
      ofType<contentActions.SearchItems>(contentActions.ContentActionTypes.SearchItems),
      mergeMap((action: any) =>
        this.contentEditorService.getFilteredContentSections(action.payload.keyword).pipe(
          map((result: ContentSection[]) => {
            const searchModel = action.payload;
            searchModel.busy = false;
            if (searchModel.result) {
              searchModel.result.templates = result.filter((item) =>
                item.ofType(action.payload.type),
              );
            } else {
              searchModel.result = {
                categories: [],
                templates: result.filter((item) => item.ofType(action.payload.type)),
              };
            }
            return new contentActions.SetSearchResult(searchModel);
          }),
          catchError((err: any) => of(new contentActions.Error(err))),
        ),
      ),
    ),
  );

  loadCategoryItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType<contentActions.LoadCategoryItem>(contentActions.ContentActionTypes.LoadCategoryItem),
      withLatestFrom(this.store.pipe(select(getState))),
      mergeMap(([incoming, state]: [contentActions.LoadCategoryItem, ContentState]) => {
        console.log('loadCategoryItem$');

        const contentSectionDetail = this.contentEditorService.getContentSectionById(
          incoming.payload,
          incoming.searchType,
        );
        const contentPagePagedDetail = this.contentEditorService.getContentPagesBySectionId(
          incoming.payload,
          incoming.searchType,
        );

        return forkJoin(contentSectionDetail, contentPagePagedDetail).pipe(
          map(([contentSection, contentSectionPages]: [ContentSection, ContentPagePaged]) => {
            return new contentActions.LoadCategoryItemSuccess({
              section: contentSection,
              pages: contentSectionPages,
            });
          }),
          catchError((err: any) => of(new contentActions.Error(err))),
        );
      }),
    ),
  );

  loadCategoriesItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType<contentActions.LoadCategoryItems>(contentActions.ContentActionTypes.LoadCategoryItems),
      mergeMap((action: contentActions.LoadCategoryItems) => {
        return this.contentEditorService.getContentSectionsByGroupId(action.payload).pipe(
          map((result: any[]) => {
            return new contentActions.LoadCategoryItemsSuccess(result); //
          }),
          catchError((err: any) => of(new contentActions.Error(err))),
        );
      }),
    ),
  );

  loadSearchTemplateItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType<contentActions.SearchTemplateItems>(
        contentActions.ContentActionTypes.SearchTemplateItems,
      ),
      map((action: contentActions.SearchTemplateItems) => action),
      withLatestFrom(this.store.pipe(select(getState))),
      switchMap(([action, state]: [contentActions.SearchTemplateItems, ContentState]) => {
        return this.contentEditorService.getContentSectionsByGroupId(action.payload).pipe(
          map((result: any) => {
            console.log(result);
            const searchItem = state.search.find((item) => item.type === action.payload.searchType);
            searchItem.result.templates = result;
            return new contentActions.SetSearchResult(searchItem);
          }),
          catchError((err: any) => of(new contentActions.Error(err))),
        );
      }),
    ),
  );

  createCategory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(contentActions.ContentActionTypes.CreateCategory),
      map((action: contentActions.CreateCategory) => action.payload),
      mergeMap((incoming) => {
        console.log('createCategory incoming', incoming);
        return this.contentEditorService.createContentSectionGroup(incoming).pipe(
          switchMap((item: ContentSectionGroup) => {
            const actions = [];
            actions.push(new contentActions.CreateCategorySuccess(item));
            return actions;
          }),
          catchError((err: any) => of(new contentActions.Error(err))),
        );
      }),
    ),
  );

  updateContentSectionGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(contentActions.ContentActionTypes.UpdateContentSectionGroup),
      map((action: contentActions.UpdateContentSectionGroup) => action.payload),
      mergeMap((incoming) => {
        return this.contentEditorService
          .updateContentSectionGroup(incoming.item, incoming.fields || null)
          .pipe(
            switchMap((item: ContentSectionGroup) => {
              const actions = [];
              actions.push(new contentActions.UpdateContentSectionGroupSuccess(item));
              actions.push(new contentActions.LoadCategories());
              return actions;
            }),
            catchError((err: any) => of(new contentActions.Error(err))),
          );
      }),
    ),
  );

  createCategoryItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(contentActions.ContentActionTypes.CreateCategoryItem),
      map((action: contentActions.CreateCategoryItem) => action),
      mergeMap((incoming) => {
        return this.contentEditorService.createContentSection(incoming.payload).pipe(
          switchMap((item: ContentSection) => {
            const actions = [];
            actions.push(new contentActions.CreateCategoryItemSuccess(item, incoming.loadInEditor));
            actions.push(new contentActions.LoadCategories());
            return actions;
          }),
          catchError((err: any) => of(new contentActions.Error(err))),
        );
      }),
    ),
  );

  createCategoryItemPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(contentActions.ContentActionTypes.CreateCategoryItemPage),
      map((action: contentActions.CreateCategoryItemPage) => action.payload),
      mergeMap((incoming) => {
        return this.contentEditorService.createContentPage(incoming).pipe(
          switchMap((item: any) => {
            const actions = [];
            //actions.push(new contentActions.LoadCategoryItem(item.section));
            actions.push(new contentActions.CreateCategoryItemPageSuccess(item));
            // if(item.section > 0) {
            //     actions.push(new contentActions.LoadCategoryItemPages(incoming.section))
            // }
            return actions;
          }),
          catchError((err: any) => of(new contentActions.Error(err))),
        );
      }),
    ),
  );

  updateContentSection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(contentActions.ContentActionTypes.UpdateContentSection),
      map((action: contentActions.UpdateContentSection) => action.payload),
      mergeMap((incoming) => {
        return this.contentEditorService.updateContentSection(incoming).pipe(
          switchMap((item: ContentSection) => {
            const actions = [];
            actions.push(new contentActions.UpdateContentSectionSuccess(item));
            actions.push(
              new contentActions.LoadCategoryItems({
                categoryID: item.section_group,
              }),
            );
            actions.push(new contentActions.LoadCategories());
            return actions;
          }),
          catchError((err: any) => of(new contentActions.Error(err))),
        );
      }),
    ),
  );

  UpdateContentPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(contentActions.ContentActionTypes.UpdateContentPage),
      map((action: contentActions.UpdateContentPage) => action.payload),
      withLatestFrom(this.store.pipe(select(getState))),
      mergeMap(([incoming, state]: [ContentPage, ContentState]) => {
        incoming.media = { ...incoming.media, ...state.mediaDataObj };
        return this.contentEditorService.updateContentPage(incoming).pipe(
          switchMap((item: ContentPage) => {
            const actions = [];
            if (!state.skipUpdateContentSection) {
              actions.push(new contentActions.UpdateContentPageSuccess(item));
            } else {
              actions.push(new contentActions.AllowUpdateContentSectionStore());
            }
            return actions;
          }),
          catchError((err: any) => of(new contentActions.Error(err))),
        );
      }),
    ),
  );

  CloneCategoryItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(contentActions.ContentActionTypes.CloneCategoryItems),
      map((action: contentActions.CloneCategoryItems) => action),
      mergeMap((incoming) => {
        const resultActions = [];
        const sectionToCreate = new ContentSection();
        sectionToCreate.name = incoming.categoryItems[0].name;
        sectionToCreate.section_group = incoming.category.id;
        sectionToCreate.id = incoming.categoryItems[0].id;

        return this.contentEditorService.cloneCategortItems(sectionToCreate).pipe(
          switchMap((contentSection: ContentSection) => {
            resultActions.push(new contentActions.CreateCategoryItemSuccess(contentSection));
            resultActions.push(new contentActions.LoadCategories());
            return resultActions;
          }),
          catchError((err: any) => of(new contentActions.Error(err))),
        );
      }),
    ),
  );

  archiveCategory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(contentActions.ContentActionTypes.ArchiveCategory),
      map((action: contentActions.ArchiveCategory) => action.payload),
      mergeMap((incoming) => {
        return this.contentEditorService.archiveCategory(incoming.id).pipe(
          switchMap((item: ContentSectionGroup) => {
            incoming.is_deleted = true;
            const actions = [];
            actions.push(new contentActions.ArchiveCategorySuccess(incoming));
            return actions;
          }),
          catchError((err: any) => of(new contentActions.Error(err))),
        );
      }),
    ),
  );

  archiveCategoryItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(contentActions.ContentActionTypes.ArchiveCategoryItem),
      map((action: contentActions.ArchiveCategoryItem) => action),
      mergeMap((action: any) => {
        return this.contentEditorService.archiveCategoryItem(action.payload.id).pipe(
          switchMap((item: ContentSection) => {
            action.payload.is_deleted = true;
            const actions = [];
            actions.push(new contentActions.ArchiveCategoryItemSuccess(action.payload));
            actions.push(new contentActions.LoadCategories());
            return actions;
          }),
          catchError((err: any) => of(new contentActions.Error(action))),
        );
      }),
    ),
  );

  archiveSectionPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(contentActions.ContentActionTypes.ArchiveSectionPage),
      map((action: contentActions.ArchiveSectionPage) => action),
      mergeMap((action: any) => {
        return this.contentEditorService.archiveSectionPage(action.payload.id).pipe(
          switchMap((item: ContentPage) => {
            action.payload.is_deleted = true;
            const actions = [];
            actions.push(new contentActions.ArchiveSectionPageSuccess(action.payload));
            return actions;
          }),
          catchError((err: any) => of(new contentActions.Error(action))),
        );
      }),
    ),
  );

  // error$ = createEffect(() =>
  //     this.actions$.pipe(
  //         ofType(contentActions.ContentActionTypes.Error),
  //         mergeMap((action: any) => {
  //             this._snackbarService.error(
  //                 action.error ? action.error : 'An error occured while processing your request.',
  //             );
  //             return EMPTY;
  //         }),
  //     ),
  // );

  restoreCategory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(contentActions.ContentActionTypes.RestoreCategory),
      map((action: contentActions.RestoreCategory) => action.payload),
      mergeMap((incoming) => {
        return this.contentEditorService.restoreCategory(incoming).pipe(
          switchMap((item: any) => {
            const actions = [];
            actions.push(new contentActions.RestoreCategorySuccess(item));
            return actions;
          }),
          catchError((err: any) => of(new contentActions.Error(err))),
        );
      }),
    ),
  );

  restoreCategoryItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(contentActions.ContentActionTypes.RestoreCategoryItem),
      map((action: contentActions.RestoreCategoryItem) => action.payload),
      mergeMap((incoming) => {
        return this.contentEditorService.restoreCategoryItem(incoming).pipe(
          switchMap((item: any) => {
            const actions = [];
            actions.push(new contentActions.RestoreCategoryItemSuccess(item));
            return actions;
          }),
          catchError((err: any) => of(new contentActions.Error(err))),
        );
      }),
    ),
  );

  selectCategories$ = createEffect(() =>
    this.actions$.pipe(
      ofType(contentActions.ContentActionTypes.SelectCategories),
      map((action: contentActions.SelectCategories) => action),
      withLatestFrom(this.store.pipe(select(getState))),
      switchMap(([incoming, state]: [contentActions.SelectCategories, ContentState]) => {
        const result = [];

        // Select/Deselect categories
        result.push(new contentActions.SelectCategoriesSuccess(incoming.selection));

        // Prevent forms load in search mode for provided search type
        if (state.search && state.search.find((model) => model.type === incoming.searchType)) {
          const currentSearchModel = state.search.find(
            (model) => model.type === incoming.searchType,
          );
          const selectedTemplate = incoming.selection[0];

          if (selectedTemplate) {
            const idx = currentSearchModel.selectedTemplates.selected.findIndex(
              (item) => item.category === selectedTemplate.category,
            );

            idx !== -1
              ? currentSearchModel.selectedTemplates.selected.splice(idx, 1)
              : currentSearchModel.selectedTemplates.selected.push(selectedTemplate);

            result.push(new contentActions.ToggleSearchResult(currentSearchModel));
          }
        } else {
          if (incoming.reset) {
            state.selectedCategories.clear();
            if (incoming.selection.length > 0) {
              result.push(
                new contentActions.LoadCategoryItems({
                  categoryID: incoming.selection[0].category,
                  searchType: incoming.searchType,
                }),
              );
            }
          } else {
            // Get categories, templates of which needs to be loaded
            const selected = incoming.selection.filter(
              (item) =>
                !state.selectedCategories.selected.find(
                  (q) => q.category === item.category && q.searchType === item.searchType,
                ),
            );

            // Load templates for each selected category
            result.push(
              ...selected.map(
                (obj) => <any>new contentActions.LoadCategoryItems({
                    categoryID: obj.category,
                    searchType: incoming.searchType,
                  }),
              ),
            );
          }
        }

        return result;
      }),
    ),
  );

  LoadCategoriesItemPages$ = createEffect(() =>
    this.actions$.pipe(
      ofType<contentActions.LoadCategoryItemPages>(
        contentActions.ContentActionTypes.LoadCategoryItemPages,
      ),
      mergeMap((action: contentActions.LoadCategoryItemPages) =>
        this.contentEditorService.getContentSectionPages(action.payload).pipe(
          map((result: ContentPagePaged) => {
            return new contentActions.LoadCategoryItemPagesSuccess(result.results); //
          }),
          catchError((err: any) => of(new contentActions.Error(err))),
        ),
      ),
    ),
  );

  processing$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          contentActions.ContentActionTypes.RestoreCategory,
          contentActions.ContentActionTypes.RestoreCategoryItem,
          contentActions.ContentActionTypes.ArchiveCategory,
          contentActions.ContentActionTypes.ArchiveCategoryItem,
          contentActions.ContentActionTypes.CreateCategory,
          contentActions.ContentActionTypes.CreateCategoryItem,
          contentActions.ContentActionTypes.CreateCategoryItemPage,
          contentActions.ContentActionTypes.UpdateContentSection,
          contentActions.ContentActionTypes.UpdateContentSectionGroup,
          contentActions.ContentActionTypes.UpdateContentPage,
          contentActions.ContentActionTypes.CloneCategoryItems,
          contentActions.ContentActionTypes.SortCategory,
          contentActions.ContentActionTypes.ArchiveSectionPage,
        ),
        mergeMap((action: any): Observable<Action> => {
          this._snackbarService.info(action.info ? action.info : 'Operation in progress...');
          return EMPTY;
        }),
      ),
    { dispatch: false },
  );

  // success$ = createEffect(() =>
  //     this.actions$.pipe(
  //         ofType(
  //             contentActions.ContentActionTypes.RestoreCategorySuccess,
  //             contentActions.ContentActionTypes.RestoreCategoryItemSuccess,
  //             contentActions.ContentActionTypes.ArchiveCategorySuccess,
  //             contentActions.ContentActionTypes.ArchiveCategoryItemSuccess,
  //             contentActions.ContentActionTypes.CreateCategorySuccess,
  //             contentActions.ContentActionTypes.CreateCategoryItemSuccess,
  //             contentActions.ContentActionTypes.CreateCategoryItemPageSuccess,
  //             contentActions.ContentActionTypes.UpdateContentSectionSuccess,
  //             contentActions.ContentActionTypes.UpdateContentSectionGroupSuccess,
  //             contentActions.ContentActionTypes.UpdateContentPageSuccess,
  //             contentActions.ContentActionTypes.CloneCategoryItemsSuccess,
  //             contentActions.ContentActionTypes.SortCategorySuccess,
  //             contentActions.ContentActionTypes.ArchiveSectionPageSuccess,
  //         ),
  //         mergeMap((action: any) => {
  //             this._snackbarService.success(action.success ? action.success : 'Operation successfully completed.');
  //             return EMPTY;
  //         }),
  //     ),
  // );

  sortTemplates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(contentActions.ContentActionTypes.SortCategoryItem),
      withLatestFrom(this.store.pipe(select(getState))),
      switchMap(([action, state]: [contentActions.SortCategoryItem, ContentState]) => {
        this._snackbarService.info('Updating template sort order...');
        const item = <any>{};
        item.id = action.payload.id;
        if (action.toIndex > 0) {
          if (action.toIndex < action.fromIndex) {
            item.position_after =
              action.toIndex > 0
                ? action.templateItems[action.toIndex - 1].id
                : state.categoryItems.find((item) => item.order < action.templateItems[0].order).id;
          } else {
            item.position_after = action.templateItems[action.toIndex].id;
          }
        } else {
          item.order = 0;
        }
        return [new contentActions.UpdateContentSection(item)];
      }),
    ),
  );

  restoreCategoryItem2$ = createEffect(() =>
    this.actions$.pipe(
      ofType(contentActions.ContentActionTypes.RestoringCategoryItem),
      withLatestFrom(this.store.pipe(select(getState))),
      switchMap(([action, state]: [any, ContentState]) => {
        this._snackbarService.info('Updating template sort order...');
        const item = new ContentSection();
        item.id = action.payload.item.id;
        item.name = action.payload.item.name;
        item.section_group = action.payload.group.section_group;
        item.is_published = action.payload.item.is_published;
        item.is_deleted = false;
        item.order = 0;
        return [new contentActions.UpdateContentSection(item)];
      }),
    ),
  );

  previewTemplateSection$ = createEffect(() =>
    this.actions$.pipe(
      ofType<contentActions.PreviewTemplateSection>(
        contentActions.ContentActionTypes.PreviewTemplateSection,
      ),
      mergeMap((action: contentActions.PreviewTemplateSection) =>
        this.contentEditorService
          .getContentPagesBySectionId(action.payload.template.id, action.payload.searchType)
          .pipe(
            map((result: ContentPagePaged) => {
              const categoryItem = {
                section: action.payload.template,
                pages: result,
                showPreview: true,
              };
              return new contentActions.LoadCategoryItemSuccess(categoryItem);
            }),
            catchError((err: any) => of(new contentActions.Error(err))),
          ),
      ),
    ),
  );

  moveTemplates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(contentActions.ContentActionTypes.MoveCategoryItem),
      withLatestFrom(this.store.pipe(select(getState))),
      switchMap(([action, state]: [contentActions.MoveCategoryItem, ContentState]) => {
        this._snackbarService.info('Moving template to other category...');
        const item: any = new ContentSection();
        item.id = action.payload.id;
        item.name = action.payload.name;
        item.section_group = action.new_section_group;
        item.is_published = action.payload.is_published;
        item.is_deleted = action.payload.is_deleted;

        if (!action.fromIndex && !action.toIndex) {
          item.position_after = null;
          item.order = 0;
        } else {
          if (action.toIndex < action.fromIndex) {
            item.position_after =
              action.toIndex > 0
                ? (<any>action.templateItems[action.toIndex - 1]).id
                : state.categoryItems.find(
                    (item: any) => item.order < action.templateItems[0].order,
                  ).id;
          } else {
            item.position_after =
              action.toIndex === 0
                ? action.payload.id
                : action.templateItems[action.toIndex - 1].id;
          }
        }
        return [new contentActions.UpdateContentSection(item)];
      }),
    ),
  );

  sortCategory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(contentActions.ContentActionTypes.SortCategory),
      withLatestFrom(this.store.pipe(select(getState))),
      switchMap(([action, state]: [contentActions.SortCategory, ContentState]) => {
        const filtered = state.categories.filter((templ) => templ.ofType(action.searchType));
        const rest = state.categories.filter((templ) => !templ.ofType(action.searchType));

        const item = new ContentSectionGroup();
        item.id = action.payload.id;
        item.name = action.payload.name;
        item.banner = action.payload.banner;
        item.shared_on_app = action.payload.shared_on_app;
        item.shared_on_web = action.payload.shared_on_web;
        if (action.toIndex > 0) {
          item.position_after = filtered[action.toIndex - 1].id;
        } else {
          item.order = 0;
        }

        moveItemInArray(filtered, action.fromIndex, action.toIndex);
        const merged = [...filtered, ...rest];
        const fields = [item.position_after ? 'position_after' : 'order'];

        return [
          new contentActions.SortCategorySuccess(merged),
          new contentActions.UpdateContentSectionGroup({ item, fields }),
        ];
      }),
    ),
  );

  sortContentPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(contentActions.ContentActionTypes.SortContentPage),
      withLatestFrom(this.store.pipe(select(getState))),
      switchMap(([action, state]: [contentActions.SortContentPage, ContentState]) => {
        const contentPages = state.sectionPages;
        const page = new ContentPage();
        page.id = action.payload.id;

        if (action.toIndex > 0) {
          page.position_after = contentPages[action.toIndex - 1].id;
        } else {
          page.order = 0;
        }

        moveItemInArray(contentPages, action.fromIndex, action.toIndex);

        return this.contentEditorService.sortContentPage(page).pipe(
          switchMap((item: ContentPage) => {
            return [new contentActions.UpdateContentPageSuccess(item)];
          }),
          catchError((err: any) => of(new contentActions.Error(err))),
        );
      }),
    ),
  );

  private getMediaWidgetModel(data: ContentPage) {}
}
